import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Images, Package, GalleryHorizontal, Building2, MapPin, MessageSquareQuote, Inbox, ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const cards = [
  { key: "sliders", label: "Sliders", icon: Images, to: "/admin/sliders", color: "bg-blue-50 text-blue-600" },
  { key: "products", label: "Products", icon: Package, to: "/admin/products", color: "bg-indigo-50 text-indigo-600" },
  { key: "gallery", label: "Gallery Images", icon: GalleryHorizontal, to: "/admin/gallery", color: "bg-violet-50 text-violet-600" },
  { key: "distributors", label: "Distributors", icon: Building2, to: "/admin/distributors", color: "bg-emerald-50 text-emerald-600" },
  { key: "machine-locations", label: "Machine Locations", icon: MapPin, to: "/admin/machine-locations", color: "bg-amber-50 text-amber-600" },
  { key: "testimonials", label: "Testimonials", icon: MessageSquareQuote, to: "/admin/testimonials", color: "bg-pink-50 text-pink-600" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    document.title = "Dashboard | Aimway Admin";
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats({}));
    api.get("/admin/enquiries").then(({ data }) => setRecent((data || []).slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-2xl font-800 text-brand-navy">Dashboard</h1>
      <p className="mt-1 text-sm text-muted-foreground">Welcome back — here is an overview of your website content.</p>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Link to="/admin/enquiries" className="rounded-2xl border border-slate-200 bg-brand p-6 text-white shadow-card transition-transform hover:-translate-y-1" data-testid="stat-enquiries">
          <div className="flex items-center justify-between">
            <Inbox size={24} />
            <ArrowUpRight size={18} className="opacity-70" />
          </div>
          <div className="mt-6 font-display text-4xl font-800">{stats ? stats.enquiries ?? 0 : <Loader2 className="animate-spin" size={22} />}</div>
          <div className="mt-1 text-sm text-blue-100">Total Enquiries {stats?.enquiries_unread ? `• ${stats.enquiries_unread} unread` : ""}</div>
        </Link>

        {cards.map((c) => (
          <Link key={c.key} to={c.to} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card transition-transform hover:-translate-y-1" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className={`grid h-11 w-11 place-items-center rounded-xl ${c.color}`}><c.icon size={20} /></span>
              <ArrowUpRight size={18} className="text-slate-300" />
            </div>
            <div className="mt-6 font-display text-4xl font-800 text-brand-navy">{stats ? stats[c.key] ?? 0 : "…"}</div>
            <div className="mt-1 text-sm text-muted-foreground">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-700 text-brand-navy">Recent Enquiries</h2>
          <Link to="/admin/enquiries" className="text-sm font-600 text-brand hover:underline">View all</Link>
        </div>
        {recent.length ? (
          <div className="divide-y divide-slate-100">
            {recent.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div>
                  <div className="font-600 text-brand-navy">{e.name} <span className="text-xs font-500 text-brand-orange">• {e.interest}</span></div>
                  <div className="text-sm text-muted-foreground">{e.email} · {e.phone}</div>
                </div>
                <div className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">No enquiries yet.</p>
        )}
      </div>
    </div>
  );
}
